import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
const API_BASE_URL = process.env.REACT_APP_URL_backend;

const RewardClaimPage = () => {
    const { id, campaign, referrer } = useParams();
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [rewardData, setRewardData] = useState(null);
    const [claimToken, setClaimToken] = useState('');
    const [rewardStatus, setRewardStatus] = useState(''); // 'unlocked', 'claimed', 'expired'
    const [expiresAt, setExpiresAt] = useState(null);
    const [understood, setUnderstood] = useState(false); // Add checkbox state
    const [currentTime, setCurrentTime] = useState(new Date()); // Add this state

    // Remove localStorage completely - always check server state
    useEffect(() => {
        if (id && campaign && referrer) {
            checkRewardStatus();
        } else {
            setError('Invalid reward URL parameters');
        }
    }, [id, campaign, referrer]);

    // Add periodic checking for claimed rewards to handle expiry
    useEffect(() => {
        let interval;
        if (rewardStatus === 'claimed' && expiresAt) {
            interval = setInterval(() => {
                const now = new Date();
                setCurrentTime(now); // Update current time to trigger re-render
                const expiry = new Date(expiresAt);
                if (now >= expiry) {
                    setRewardStatus('expired');
                    setRewardData(null);
                    setExpiresAt(null);
                    clearInterval(interval);
                }
            }, 1000); // Check every second for real-time updates
        }
        
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [rewardStatus, expiresAt]);

    const checkRewardStatus = async () => {
        try {
            setLoading(true);
            setError('');
                        
            const response = await fetch(`${API_BASE_URL}/api/rewards/${id}/${campaign}/${referrer}`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({})
            });
                        
            // Handle 204 No Content (expired reward)
            if (response.status === 204) {
                setRewardStatus('expired');
                return;
            }
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error('API Error Response:', errorText);
                throw new Error(`HTTP ${response.status}: ${errorText || 'Network response was not ok'}`);
            }
            
            const data = await response.json();
            
            if (data.success && data.status === 'unlocked') {
                setClaimToken(data.claimToken);
                setRewardStatus('unlocked');
            } else if (data.success && data.status === 'claimed') {
                setRewardData(data.rewardData);
                setExpiresAt(data.expiresAt);
                setRewardStatus('claimed');
            } else {
                setError(data.error || 'Reward not available');
            }
        } catch (err) {
            console.error('Failed to check reward status:', err);
            
            if (err.message.includes('Failed to fetch')) {
                setError('Network error. Please check your connection and try again.');
            } else if (err.message.includes('404')) {
                setError('Reward not found. Please check the URL.');
            } else if (err.message.includes('500')) {
                setError('Server error. Please try again later.');
            } else {
                setError(err.message || 'Failed to check reward status');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleClaimReward = async () => {
        if (!claimToken) {
            setError('No valid claim token available');
            return;
        }

        try {
            setLoading(true);
            setError('');
            
            startDealTimer();
                        
            const response = await fetch(`${API_BASE_URL}/api/rewards/claim-reward`, {
                method: 'POST',
                headers: { 
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ claimToken })
            });
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error('Claim Error Response:', errorText);
                throw new Error(`HTTP ${response.status}: ${errorText || 'Network response was not ok'}`);
            }
            
            const data = await response.json();
            
            if (data.success) {
                // Immediately check status after successful claim
                await checkRewardStatus();
            } else {
                setError(data.error || 'Failed to claim reward');
            }
        } catch (err) {
            console.error('Failed to claim reward:', err);
            setError(err.message || 'Failed to claim reward');
        } finally {
            setLoading(false);
        }
    };

    const startDealTimer = () => {
        console.log('Deal timer started!');
    };

    const formatTimeRemaining = () => {
        if (!expiresAt) return '';
        
        const now = new Date();
        const expiry = new Date(expiresAt);
        const diff = expiry - now;
        
        if (diff <= 0) return 'Expired';
        
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        
        if (hours > 0) {
            return `${hours}h ${minutes}m ${seconds}s Remaining`;
        } else if (minutes > 0) {
            return `${minutes}m ${seconds}s Remaining`;
        } else {
            return `${seconds}s Remaining`;
        }
    };

    // Loading state
    if (loading && !rewardData) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-white">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2b0062]"></div>
                <span className="ml-3 text-[#2b0062]">Checking reward status...</span>
            </div>
        );
    }

    // Error state
    if (error && !rewardData) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="max-w-md mx-auto p-6 bg-white border border-[#2b0062] rounded-lg">
                    <h2 className="text-xl font-bold text-[#2b0062] mb-2">⚠️ Error</h2>
                    <p className="text-[#2b0062] mb-4">{error}</p>
                    <div className="mb-4 p-3 bg-white border border-[#2b0062] text-[#2b0062] rounded text-sm">
                        There was an issue checking your reward status. Please try again later.
                    </div>
                    <button
                        onClick={checkRewardStatus}
                        className="w-full bg-[#2b0062] hover:bg-[#2b0062]/90 text-white font-bold py-2 px-4 rounded transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    // Expired state
    if (rewardStatus === 'expired') {
        return (
            <div className="min-h-screen bg-white flex flex-col items-center justify-between p-6">
                {/* Header */}
                <div className="flex-1 flex items-start justify-center pt-16">
                    <h2 className="text-3xl font-bold text-[#2b0062] text-center">⏰ Reward Expired</h2>
                </div>

                {/* Center message */}
                <div className="flex-1 flex items-center justify-center">
                    <div className="bg-white border-4 border-[#2b0062] rounded-full px-12 py-8 max-w-md text-center shadow-lg">
                        <p className="text-lg font-semibold text-[#2b0062] leading-relaxed mb-4">
                            This reward has expired and is no longer available.
                        </p>
                        <div className="mt-4 p-3 bg-[#2b0062]/5 rounded-lg">
                            <p className="text-[#2b0062]/80 italic text-lg">Thank you for your support!</p>
                        </div>
                    </div>
                </div>

                {/* Bottom section with expired clock */}
                <div className="flex-1 flex flex-col items-center justify-end pb-16">
                    {/* Expired Clock Icon */}
                    <div className="relative w-24 h-24 mb-4">
                        <svg className="w-full h-full" viewBox="0 0 100 100">
                            {/* Clock face */}
                            <circle
                                cx="50"
                                cy="50"
                                r="45"
                                fill="none"
                                stroke="#2b0062"
                                strokeWidth="3"
                                opacity="0.5"
                            />
                            {/* X mark over clock */}
                            <line
                                x1="30"
                                y1="30"
                                x2="70"
                                y2="70"
                                stroke="#2b0062"
                                strokeWidth="4"
                                strokeLinecap="round"
                            />
                            <line
                                x1="70"
                                y1="30"
                                x2="30"
                                y2="70"
                                stroke="#2b0062"
                                strokeWidth="4"
                                strokeLinecap="round"
                            />
                            {/* Clock numbers (dots) - faded */}
                            <circle cx="50" cy="10" r="2" fill="#2b0062" opacity="0.5" />
                            <circle cx="90" cy="50" r="2" fill="#2b0062" opacity="0.5" />
                            <circle cx="50" cy="90" r="2" fill="#2b0062" opacity="0.5" />
                            <circle cx="10" cy="50" r="2" fill="#2b0062" opacity="0.5" />
                        </svg>
                    </div>
                    
                    {/* Expired text */}
                    <div className="text-center">
                        <div className="text-xl font-mono font-bold text-[#2b0062] mb-2">
                            Expired
                        </div>
                        <p className="text-sm text-[#2b0062]/70">
                            Reward is no longer valid
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Claimed state - show reward with live countdown
    if (rewardStatus === 'claimed' && rewardData) {
        return (
            <div className="min-h-screen bg-white flex flex-col items-center justify-between p-6">
                {/* Header */}
                <div className="flex-1 flex items-start justify-center pt-16">
                    <h2 className="text-3xl font-bold text-[#2b0062] text-center">🎉 Reward Claimed!</h2>
                </div>

                {/* Center oval with reward text */}
                <div className="flex-1 flex items-center justify-center">
                    <div className="bg-white border-4 border-[#2b0062] rounded-full px-12 py-8 max-w-md text-center shadow-lg pt-4">
                        <p className="text-lg italic text-[#2b0062] leading-relaxed">
                            Show this to the bartender to claim your reward!
                        </p>
                        <div className="mt-4 p-3 bg-[#2b0062]/5 rounded-lg">
                            <p className="text-[#2b0062] font-bold text-xl">{rewardData}</p>
                        </div>
                    </div>
                </div>

                {/* Bottom section with clock and timer */}
                <div className="flex-1 flex flex-col items-center justify-end pb-16">
                    {expiresAt && (
                        <>
                            {/* Minimalistic Clock */}
                            <div className="relative w-24 h-24 mb-4">
                                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                                    {/* Clock face */}
                                    <circle
                                        cx="50"
                                        cy="50"
                                        r="45"
                                        fill="none"
                                        stroke="#2b0062"
                                        strokeWidth="3"
                                    />
                                    {/* Clock hands */}
                                    <line
                                        x1="50"
                                        y1="50"
                                        x2="50"
                                        y2="20"
                                        stroke="#2b0062"
                                        strokeWidth="3"
                                        strokeLinecap="round"
                                    />
                                    <line
                                        x1="50"
                                        y1="50"
                                        x2="70"
                                        y2="50"
                                        stroke="#2b0062"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                    />
                                    {/* Center dot */}
                                    <circle cx="50" cy="50" r="3" fill="#2b0062" />
                                    {/* Clock numbers (dots) */}
                                    <circle cx="50" cy="10" r="2" fill="#2b0062" />
                                    <circle cx="90" cy="50" r="2" fill="#2b0062" />
                                    <circle cx="50" cy="90" r="2" fill="#2b0062" />
                                    <circle cx="10" cy="50" r="2" fill="#2b0062" />
                                </svg>
                            </div>
                            
                            {/* Timer text */}
                            <div className="text-center">
                                <div className="text-xl font-mono font-bold text-[#2b0062] mb-2">
                                    {formatTimeRemaining()}
                                </div>
                                <p className="text-sm text-[#2b0062]/70">
                                    Before Reward Expires
                                </p>
                            </div>
                        </>
                    )}
                    
                </div>
            </div>
        );
    }

    // Unlocked state
    if (rewardStatus === 'unlocked') {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="max-w-md mx-auto p-6 bg-white border border-[#2b0062] rounded-lg">
                    <h2 className="text-2xl font-bold text-[#2b0062] mb-4">🎉 Reward Unlocked!</h2>
                    <p className="text-[#2b0062] mb-6">You've unlocked a reward! Click claim to reveal your reward.</p>
                    
                    <div className="bg-white p-4 rounded-lg border border-[#2b0062] mb-6">
                        <h3 className="font-semibold text-[#2b0062] mb-2">Click to Claim!</h3>
                        <p className="text-[#2b0062] mb-4">Rewards are time gated, please wait until you're ready to use to claim!</p>
                        
                        <div className="flex items-center space-x-2">
                            <input
                                type="checkbox"
                                id="understand-checkbox"
                                checked={understood}
                                onChange={(e) => setUnderstood(e.target.checked)}
                                className="w-4 h-4 text-[#2b0062] bg-white border-[#2b0062] rounded focus:ring-[#2b0062] focus:ring-2"
                            />
                            <label htmlFor="understand-checkbox" className="text-sm text-[#2b0062] cursor-pointer">
                                I understand
                            </label>
                        </div>
                    </div>
                    
                    {error && (
                        <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded">
                            {error}
                        </div>
                    )}
                    
                    <button
                        onClick={handleClaimReward}
                        disabled={loading || !understood}
                        className="w-full bg-[#2b0062] hover:bg-[#2b0062]/90 disabled:bg-[#2b0062]/50 disabled:cursor-not-allowed text-white font-bold py-3 px-4 rounded-lg transition-colors"
                    >
                        {loading ? (
                            <span className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Claiming...
                            </span>
                        ) : (
                            'Claim Reward'
                        )}
                    </button>
                </div>
            </div>
        );
    }

    // Fallback state
    return (
        <div className="min-h-screen bg-white flex items-center justify-center">
            <div className="max-w-md mx-auto p-6 bg-white border border-[#2b0062] rounded-lg">
                <h2 className="text-xl font-bold text-[#2b0062] mb-2">🔍 No Reward Found</h2>
                <p className="text-[#2b0062] mb-4">This reward link appears to be invalid or expired.</p>
                <div className="mb-4 p-3 bg-white border border-[#2b0062] text-[#2b0062] rounded text-sm">
                    <strong>URL Parameters:</strong><br/>
                    ID: {id || 'Missing'}<br/>
                    Campaign: {campaign || 'Missing'}<br/>
                    Referrer: {referrer || 'Missing'}
                </div>
                <button
                    onClick={checkRewardStatus}
                    className="w-full bg-[#2b0062] hover:bg-[#2b0062]/90 text-white font-bold py-2 px-4 rounded transition-colors"
                >
                    Check Again
                </button>
            </div>
        </div>
    );
};

export default RewardClaimPage;